import { useEffect, useRef, useState } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fromEvent } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";
import { RootState, AppDispatch } from "../../reduxStore/Store";
import { AutocompleteProps } from "@mui/material/Autocomplete";
import { Article } from "../../models/models";

import {
  fetchArticleByFilters,
  clearArticleData,
} from "../../reduxStore/ArticleSlice"; // make sure this exists and is correctly exported

interface ArticleAutoCProps
  extends Partial<AutocompleteProps<Article, false, false, false>> {
  onArticleChange: (article: Article | null) => void;
  selectedArticle?: Article | null;
  externalRef?: React.RefObject<HTMLInputElement | null>; // permet au parent de focus le champ
  /** Override explicite du département pour la résolution du prix (sinon le
   *  département actif du contexte, injecté par l'intercepteur axios). Utile
   *  quand le prix doit suivre un département différent (ex: departement_id du
   *  document en cours, pertinent en mode "Tous départements"). */
  departement_id?: number | null;
}

const limit = 10;
const offset = 0;

const ArticleAutoC: React.FC<ArticleAutoCProps> = ({
  onArticleChange,
  selectedArticle,
  externalRef,
  departement_id,
  ...autocompleteProps
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { articleData, loading } = useSelector((state: RootState) => state.ArticleSlice);
  const [article, setArticle] = useState<Article | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (selectedArticle) {
      setArticle(selectedArticle);
    }
  }, [selectedArticle]);

  useEffect(() => {
    if (inputRef.current) {
      const subscription = fromEvent(inputRef.current, "input")
        .pipe(
          debounceTime(500),
          switchMap((event: any) => {
            const nom_article = event.target.value.trim();
            if (nom_article.length > 2) {
              dispatch(fetchArticleByFilters({ code: nom_article, designation: nom_article, visible: null, departement_id, limit, offset }));
            }
            return [];
          })
        )
        .subscribe();

      return () => subscription.unsubscribe();
    }
  }, [dispatch, departement_id]);

  const handleChange = (_: any, newValue: Article | null) => {
    setArticle(newValue);
    onArticleChange(newValue);
  };

  return (
    <Autocomplete
      options={articleData}
      value={article}
      // Présélectionne la première option filtrée : Enter la valide au lieu de laisser
      // remonter l'événement au formulaire parent. Sans ça, taper un code puis Enter
      // soumet la ligne avec article_id à 0 (saisie clavier — le formulaire de ligne
      // redonne le focus ici après chaque ajout et soumet sur Enter).
      autoHighlight
      getOptionLabel={(option: Article) => `${option.code} - ${option.designation}`}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      onChange={handleChange}
      onBlur={() => dispatch(clearArticleData())}
      {...autocompleteProps}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={(el) => {
            (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
            if (externalRef) (externalRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
          }}
          label="Article"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          size="small"
        />
      )}
    />
  );
};

export default ArticleAutoC;
